import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Atom, BookOpen, CheckCircle2, ChevronLeft, ChevronRight, Download, FlaskConical, GraduationCap, Lightbulb, PenLine, Plus, Target, Trash2, UploadCloud } from "lucide-react";
import { HomeworkShell, useHwAccount } from "./HomeworkShell";
import { hwApi, hwErr } from "./hwApi";
import { hwdApi } from "./HomeworkHome";

const API = process.env.REACT_APP_BACKEND_URL;

const CLASS_META = {
  class_6: { icon: BookOpen, color: "#0891b2", bg: "#ecfeff" },
  class_7: { icon: PenLine, color: "#7c3aed", bg: "#f5f3ff" },
  class_8: { icon: Lightbulb, color: "#db2777", bg: "#fdf2f8" },
  class_9: { icon: FlaskConical, color: "#059669", bg: "#ecfdf5" },
  class_10: { icon: Target, color: "#d97706", bg: "#fffbeb" },
  class_11: { icon: Atom, color: "#1d4ed8", bg: "#eff6ff" },
  class_12: { icon: GraduationCap, color: "#dc2626", bg: "#fef2f2" },
};
const CLASSES = ["class_6", "class_7", "class_8", "class_9", "class_10", "class_11", "class_12"];
const CLASS_LABEL = { class_6: "Class 6", class_7: "Class 7", class_8: "Class 8", class_9: "Class 9", class_10: "Class 10", class_11: "Class 11", class_12: "Class 12" };
const Q_STATUSES = [["solved_confidently", "Solved confidently"], ["attempted_unsure", "Attempted but unsure"],
                    ["not_attempted", "Not attempted"], ["doubt", "Needs a doubt explanation"]];
const SLOTS = [
  ["question_paper", "Upload Question Paper PDF", "The original questions you are attempting (from your book, worksheet or test paper)."],
  ["attempted", "Upload My Solved Homework PDF", "Your handwritten answers and calculations — this is what Pranjal Sir evaluates."],
  ["solution", "Upload Solution PDF — Optional", "Answer key / reference solutions, if you have them. Optional — you can submit without it."],
];
const STATUS_LABEL = { draft: "DRAFT", submitted: "WITH SIR", reviewing: "BEING CHECKED", evaluated: "CHECKED",
                       returned_for_correction: "CORRECTIONS REQUESTED", resubmission_requested: "RESUBMIT" };

function FlowInner() {
  const account = useHwAccount();
  const [params] = useSearchParams();
  const [step, setStep] = useState("class"); // class | subject | scope | workspace | review | done
  const [category, setCategory] = useState(account.category || "");
  const [subject, setSubject] = useState("");
  const [chapters, setChapters] = useState([]);
  const [chapter, setChapter] = useState("");
  const [topic, setTopic] = useState("");
  const [subtopic, setSubtopic] = useState("");
  const [sub, setSub] = useState(null); // the draft/submission doc
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");
  const [paper, setPaper] = useState(null);
  const fileRefs = { question_paper: useRef(null), attempted: useRef(null), solution: useRef(null) };

  // arriving from the Study Planner: jump straight to chapter/topic with the planner's
  // chapter preselected, and remember which timetable block to tick off on submit
  const plannerRef = useMemo(() => {
    const d = params.get("planner_day");
    const s = params.get("planner_slot");
    return d !== null && s ? { day_idx: Number(d), slot_id: s } : null;
  }, [params]);
  useEffect(() => {
    if (!plannerRef || params.get("resume") || !category) return;
    setSubject("Physics");
    setStep("scope");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plannerRef, category]);

  // load syllabus when class chosen
  useEffect(() => {
    if (!category) return;
    hwApi.get("/syllabus", { params: { category } })
      .then(({ data }) => {
        setChapters(data.chapters || []);
        const want = params.get("chapter");
        if (plannerRef && want && (data.chapters || []).some((c) => c.chapter === want)) setChapter(want);
      })
      .catch(() => setError("Could not load chapters for this class."));
  }, [category]);

  // resume an existing draft (?resume=<id>)
  useEffect(() => {
    const rid = params.get("resume");
    if (!rid) return;
    hwdApi.get(`/submissions/${rid}`).then(({ data }) => {
      setSub(data);
      setCategory(data.category);
      setSubject(data.subject);
      setChapter(data.chapter);
      setTopic(data.topic);
      setSubtopic(data.subtopic || "");
      setQuestions(data.question_statuses || []);
      setStep("workspace");
    }).catch(() => setError("Could not open that homework draft."));
  }, [params]);

  const topics = useMemo(() => (chapters.find((c) => c.chapter === chapter)?.topics) || [], [chapters, chapter]);
  const subtopics = useMemo(() => (topics.find((t) => t.topic === topic)?.subtopics) || [], [topics, topic]);
  const docs = sub?.documents || {};
  const editable = sub && ["draft", "returned_for_correction", "resubmission_requested"].includes(sub.status);
  const summary = useMemo(() => {
    const c = { solved_confidently: 0, attempted_unsure: 0, not_attempted: 0, doubt: 0 };
    questions.forEach((q) => { if (c[q.status] !== undefined) c[q.status] += 1; });
    return { total: questions.length, ...c };
  }, [questions]);

  const startWorkspace = async () => {
    setError("");
    if (sub) { setStep("workspace"); return; }
    setBusy("create");
    try {
      const { data } = await hwdApi.post("/submissions", { category, chapter, topic, subtopic, ...(plannerRef ? { planner_ref : plannerRef } : {}) });
      setSub(data);
      setQuestions([]);
      setStep("workspace");
    } catch (e) { setError(hwErr(e)); } finally { setBusy(""); }
  };

  const generatePaper = async () => {
    setError("");
    setBusy("generate");
    try {
      const { data } = await hwdApi.post("/papers/generate", { chapter, topic });
      setPaper(data);
      setNotice(`Practice paper ready — ${data.count} fresh questions on ${data.topic}. Download, solve, and upload below.`);
      setTimeout(() => setNotice(""), 5000);
    } catch (e) { setError(hwErr(e)); } finally { setBusy(""); }
  };

  const uploadDoc = async (slot, files) => {
    if (!files?.length) return;
    setBusy(slot);
    setError("");
    try {
      const form = new FormData();
      for (const f of files) form.append("files", f);
      const { data } = await hwdApi.post(`/submissions/${sub.submission_id}/documents/${slot}`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      setSub((s) => ({ ...s, documents: { ...s.documents, [slot]: { name: data.name, pages: data.pages, provider: "student" } } }));
      setNotice(`${data.name} uploaded — ${data.pages} page(s).`);
      setTimeout(() => setNotice(""), 3500);
    } catch (e) { setError(hwErr(e)); } finally {
      setBusy("");
      if (fileRefs[slot].current) fileRefs[slot].current.value = "";
    }
  };

  const removeDoc = async (slot) => {
    try {
      await hwdApi.delete(`/submissions/${sub.submission_id}/documents/${slot}`);
      setSub((s) => ({ ...s, documents: { ...s.documents, [slot]: null } }));
    } catch (e) { setError(hwErr(e)); }
  };

  const movePage = async (slot, page, dir) => {
    const pages = docs[slot]?.pages || 0;
    const to = page + dir;
    if (to < 0 || to >= pages) return;
    const order = Array.from({ length: pages }, (_, i) => i);
    [order[page], order[to]] = [order[to], order[page]];
    try {
      await hwdApi.post(`/submissions/${sub.submission_id}/documents/${slot}/reorder`, { order });
      setNotice(`Page ${page + 1} moved ${dir < 0 ? "up" : "down"}.`);
      setTimeout(() => setNotice(""), 2500);
    } catch (e) { setError(hwErr(e)); }
  };

  const saveQuestions = async () => {
    setBusy("questions");
    try {
      await hwdApi.put(`/submissions/${sub.submission_id}/questions`, { questions });
      setNotice("Question status saved.");
      setTimeout(() => setNotice(""), 2500);
    } catch (e) { setError(hwErr(e)); } finally { setBusy(""); }
  };

  const finalSubmit = async () => {
    if (!window.confirm("Submit homework for evaluation? The documents will be locked while Pranjal Sir reviews them.")) return;
    setBusy("submit");
    setError("");
    try {
      await hwdApi.put(`/submissions/${sub.submission_id}/questions`, { questions });
    } catch (e) { setError(`Could not save your question tracking: ${hwErr(e)}`); setBusy(""); return; }
    try {
      await hwdApi.post(`/submissions/${sub.submission_id}/submit`);
      setStep("done");
    } catch (e) { setError(hwErr(e)); } finally { setBusy(""); }
  };

  const crumb = (
    <div className="admin-row" style={{ flexWrap: "wrap", gap: 6, marginTop: 8 }} data-testid="hwf-breadcrumb">
      {category && <span className="status-pill review">{CLASS_LABEL[category]}</span>}
      {subject && <span className="status-pill review">{subject}</span>}
      {chapter && <span className="status-pill review">{chapter}</span>}
      {topic && <span className="status-pill review">{topic}</span>}
      {subtopic && <span className="status-pill review">{subtopic}</span>}
      {step !== "class" && step !== "done" && (
        <button className="tp-btn small ghost" data-testid="hwf-start-over"
                onClick={() => { setStep("class"); setChapter(""); setTopic(""); setSubtopic(""); setSub(null); setQuestions([]); }}>
          <ArrowLeft size={13} /> Change selection</button>
      )}
    </div>
  );

  return (
    <div className="tp-wrap" style={{ maxWidth: 860 }} data-testid="hw-flow">
      <div className="tp-eyebrow">HOMEWORK &amp; PERSONAL EVALUATION</div>
      {crumb}
      {error && <p className="tp-error" role="alert" data-testid="hwf-error">{error}</p>}
      {notice && <p className="tp-pill ok" data-testid="hwf-notice">{notice}</p>}

      {step === "class" && (
        <>
          <h1 className="tp-h2" style={{ fontSize: 28, marginTop: 12 }}>Select your class</h1>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 14, marginTop: 16 }}>
            {CLASSES.map((c) => {
              const M = CLASS_META[c];
              const Icon = M.icon;
              return (
                <button key={c} onClick={() => { setCategory(c); setStep("subject"); }} data-testid={`hwf-class-${c}`}
                        style={{ cursor: "pointer", border: `2px solid ${M.color}`, background: M.bg, borderRadius: 16,
                                 padding: "22px 14px", textAlign: "center", transition: "transform .2s ease" }}>
                  <Icon size={30} style={{ color: M.color }} />
                  <div style={{ fontWeight: 700, color: "#0b2545", marginTop: 8, fontSize: 17 }}>{CLASS_LABEL[c]}</div>
                  <div className="tp-muted" style={{ fontSize: 11 }}>Physics</div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {step === "subject" && (
        <>
          <h1 className="tp-h2" style={{ fontSize: 28, marginTop: 12 }}>Select subject</h1>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 14, marginTop: 16 }}>
            <button onClick={() => { setSubject("Physics"); setStep("scope"); }} data-testid="hwf-subject-physics"
                    style={{ cursor: "pointer", border: "2px solid #1d4ed8", background: "#eff6ff", borderRadius: 16, padding: "24px 16px", textAlign: "center" }}>
              <Atom size={30} style={{ color: "#1d4ed8" }} />
              <div style={{ fontWeight: 700, color: "#0b2545", marginTop: 8, fontSize: 17 }}>Physics</div>
              <div className="tp-muted" style={{ fontSize: 11 }}>{CLASS_LABEL[category]}</div>
            </button>
          </div>
          <button className="tp-back" style={{ marginTop: 14, background: "none", border: "none", cursor: "pointer" }} onClick={() => setStep("class")} data-testid="hwf-back-class">← Change class</button>
        </>
      )}

      {step === "scope" && (
        <>
          <h1 className="tp-h2" style={{ fontSize: 28, marginTop: 12 }}>Choose chapter, topic &amp; subtopic</h1>
          {!chapter && (
            <div style={{ marginTop: 14 }} data-testid="hwf-chapters">
              {chapters.map((c) => (
                <button key={c.chapter} className="tp-panel" onClick={() => setChapter(c.chapter)} data-testid={`hwf-chapter-${c.chapter.replace(/\W+/g, "-")}`}
                        style={{ display: "block", width: "100%", textAlign: "left", marginTop: 8, cursor: "pointer", border: "1px solid #dbe7f7" }}>
                  <b style={{ color: "#0b2545" }}>{c.chapter}</b>
                  <span className="tp-muted" style={{ marginLeft: 8, fontSize: 12 }}>{c.topics.length} topics</span>
                </button>
              ))}
              {chapters.length === 0 && <p className="tp-muted">No chapters configured for this class yet.</p>}
            </div>
          )}
          {chapter && !topic && (
            <div style={{ marginTop: 14 }} data-testid="hwf-topics">
              <button className="tp-back" style={{ background: "none", border: "none", cursor: "pointer" }} onClick={() => setChapter("")} data-testid="hwf-back-chapters">← All chapters</button>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                {topics.map((t) => (
                  <button key={t.syllabus_id} className="tp-slot" style={{ cursor: "pointer" }}
                          onClick={() => setTopic(t.topic)} data-testid={`hwf-topic-${t.syllabus_id}`}>{t.topic}</button>
                ))}
              </div>
            </div>
          )}
          {chapter && topic && (
            <div style={{ marginTop: 14 }} data-testid="hwf-subtopics">
              <button className="tp-back" style={{ background: "none", border: "none", cursor: "pointer" }} onClick={() => { setTopic(""); setSubtopic(""); }} data-testid="hwf-back-topics">← All topics in {chapter}</button>
              {subtopics.length > 0 && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                  {subtopics.map((st) => (
                    <button key={st} className="tp-slot" data-testid={`hwf-subtopic-${st.replace(/\W+/g, "-")}`}
                            style={{ cursor: "pointer", border: subtopic === st ? "1.5px solid #1d4ed8" : undefined, background: subtopic === st ? "#eff6ff" : undefined }}
                            onClick={() => setSubtopic(subtopic === st ? "" : st)}>{st}</button>
                  ))}
                </div>
              )}
              <p className="tp-muted" style={{ fontSize: 13, marginTop: 10 }}>{subtopic ? `Subtopic: ${subtopic}` : "Whole topic selected — pick a subtopic above, or continue with the full topic."}</p>
              <div className="tp-panel" style={{ marginTop: 14, border: "1.5px solid #f77f00", background: "#fff7ed" }} data-testid="hwf-generate-paper">
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <b style={{ color: "#9a3412", fontSize: 14 }}>Need a fresh practice paper?</b>
                    <p className="tp-muted" style={{ fontSize: 12, margin: "2px 0 0" }}>Get 10 new questions on <b>{topic}</b> as a PDF — solve them, then upload your work below.</p>
                  </div>
                  <button className="tp-btn" style={{ background: "#f77f00" }} disabled={busy === "generate"} onClick={generatePaper} data-testid="hwf-generate-btn">
                    {busy === "generate" ? "Generating…" : "Generate Practice Paper"}
                  </button>
                </div>
                {paper && (
                  <div style={{ marginTop: 10, padding: "10px 12px", background: "#fff", borderRadius: 8, border: "1px solid #fed7aa" }} data-testid="hwf-paper-ready">
                    <div className="admin-row" style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13 }}><b>{paper.count} questions</b> on {paper.topic} · {paper.chapter}</span>
                      <a className="tp-btn small" href={paper.download_url} target="_blank" rel="noreferrer" data-testid="hwf-paper-download">Download PDF</a>
                    </div>
                    <p className="tp-muted" style={{ fontSize: 11, margin: "6px 0 0" }}>Solve all questions, then upload the same paper (or a new one) in the workspace below.</p>
                  </div>
                )}
              </div>
              <button className="tp-btn" style={{ marginTop: 10 }} disabled={busy === "create"} onClick={startWorkspace} data-testid="hwf-continue">
                {busy === "create" ? "Preparing…" : "Continue to upload"} <ArrowRight size={15} />
              </button>
            </div>
          )}
        </>
      )}

      {step === "workspace" && sub && (
        <>
          <h1 className="tp-h2" style={{ fontSize: 26, marginTop: 12 }}>{sub.title}</h1>
          <p className="tp-muted" style={{ fontSize: 13 }}>Upload each document separately, mark your question status, then review &amp; submit.</p>

          {SLOTS.map(([slot, label, hint]) => {
            const d = docs[slot];
            const optional = slot === "solution";
            return (
              <div className="tp-panel" key={slot} style={{ marginTop: 14, border: d ? "1.5px solid #12b76a" : "2px dashed #1d4ed8" }} data-testid={`hwf-doc-${slot}`}>
                <div className="admin-row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                  <div>
                    <b style={{ color: "#0b2545" }}>{label}</b>
                    <p className="tp-muted" style={{ margin: "4px 0 0", fontSize: 12 }}>{hint}</p>
                  </div>
                  {d && <span className="status-pill approved" data-testid={`hwf-doc-ok-${slot}`}>UPLOADED</span>}
                  {!d && optional && <span className="status-pill locked">OPTIONAL</span>}
                </div>
                {d && (
                  <div className="admin-row" style={{ marginTop: 10, flexWrap: "wrap", gap: 8 }} data-testid={`hwf-doc-info-${slot}`}>
                    <span style={{ color: "#1e3a5f", fontSize: 13 }}>{d.name} · {d.pages} page(s)</span>
                    <a className="tp-btn small ghost" href={`${API}/api/hwd/files/${sub.submission_id}/${slot}`} target="_blank" rel="noreferrer" data-testid={`hwf-preview-${slot}`}><Download size={13} /> Preview</a>
                    {editable && <button className="tp-btn small ghost" onClick={() => fileRefs[slot].current?.click()} data-testid={`hwf-replace-${slot}`}>Replace</button>}
                    {editable && <button className="tp-btn small ghost" onClick={() => removeDoc(slot)} data-testid={`hwf-remove-${slot}`}><Trash2 size={13} /></button>}
                  </div>
                )}
                {d && d.pages > 1 && editable && (
                  <div style={{ marginTop: 8 }} data-testid={`hwf-pages-${slot}`}>
                    <span className="tp-muted" style={{ fontSize: 11 }}>Page order: </span>
                    {Array.from({ length: d.pages }, (_, i) => (
                      <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 2, marginRight: 6, background: "#eef4ff", borderRadius: 6, padding: "2px 6px", fontSize: 11 }}>
                        p{i + 1}
                        <button style={{ border: "none", background: "none", cursor: "pointer", padding: 0 }} onClick={() => movePage(slot, i, -1)} data-testid={`hwf-pgup-${slot}-${i}`}><ChevronLeft size={11} /></button>
                        <button style={{ border: "none", background: "none", cursor: "pointer", padding: 0 }} onClick={() => movePage(slot, i, 1)} data-testid={`hwf-pgdn-${slot}-${i}`}><ChevronRight size={11} /></button>
                      </span>
                    ))}
                  </div>
                )}
                {editable && (
                  <>
                    <input ref={fileRefs[slot]} type="file" accept=".pdf,.jpg,.jpeg,.png" multiple={slot !== "solution"}
                           onChange={(e) => uploadDoc(slot, Array.from(e.target.files))} data-testid={`hwf-input-${slot}`} style={{ display: "none" }} />
                    {!d && (
                      <button type="button" className="tp-btn ghost" style={{ width: "100%", padding: 14, marginTop: 10, border: "1.5px solid #1d4ed8", background: "#fff" }}
                              disabled={busy === slot} onClick={() => fileRefs[slot].current?.click()} data-testid={`hwf-upload-${slot}`}>
                        <UploadCloud size={17} /> {busy === slot ? "Uploading…" : "Upload PDF or page photos"}
                      </button>
                    )}
                  </>
                )}
              </div>
            );
          })}

          <div className="tp-panel" style={{ marginTop: 14 }} data-testid="hwf-questions">
            <div className="admin-row" style={{ justifyContent: "space-between" }}>
              <b style={{ color: "#0b2545" }}>Question attempt status</b>
              {editable && (
                <button className="tp-btn small ghost" onClick={() => setQuestions((q) => [...q, { qno: `Q${q.length + 1}`, status: "solved_confidently", note: "" }])} data-testid="hwf-add-question">
                  <Plus size={13} /> Add question</button>
              )}
            </div>
            <p className="tp-muted" style={{ fontSize: 12 }}>Use the question numbers exactly as they appear in your uploaded question paper. Add rows manually if needed.</p>
            {questions.map((q, i) => (
              <div className="admin-row" key={i} style={{ borderTop: "1px solid #e4eaf2", padding: "7px 0", flexWrap: "wrap", gap: 8 }} data-testid={`hwf-q-${i}`}>
                <input className="admin-input" style={{ maxWidth: 80, background: "#fff", color: "#0b2545", border: "1px solid #cbd5e1" }}
                       value={q.qno} disabled={!editable} data-testid={`hwf-qno-${i}`}
                       onChange={(e) => setQuestions((c) => c.map((x, j) => (j === i ? { ...x, qno: e.target.value } : x)))} />
                <select className="admin-input" style={{ maxWidth: 220, background: "#fff", color: "#0b2545", border: "1px solid #cbd5e1" }}
                        value={q.status} disabled={!editable} data-testid={`hwf-qstatus-${i}`}
                        onChange={(e) => setQuestions((c) => c.map((x, j) => (j === i ? { ...x, status: e.target.value } : x)))}>
                  {Q_STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <input className="admin-input" style={{ flex: 1, minWidth: 140, background: "#fff", color: "#0b2545", border: "1px solid #cbd5e1" }}
                       placeholder="Short note (optional)" value={q.note} disabled={!editable} data-testid={`hwf-qnote-${i}`}
                       onChange={(e) => setQuestions((c) => c.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)))} />
                {editable && <button className="tp-btn small ghost" onClick={() => setQuestions((c) => c.filter((_, j) => j !== i))} data-testid={`hwf-qdel-${i}`}><Trash2 size={12} /></button>}
              </div>
            ))}
            {questions.length === 0 && <p className="tp-muted" style={{ fontSize: 13 }} data-testid="hwf-q-empty">No questions tracked yet — add one row per question in your paper.</p>}
            {editable && questions.length > 0 && (
              <button className="tp-btn small ghost" style={{ marginTop: 8 }} disabled={busy === "questions"} onClick={saveQuestions} data-testid="hwf-save-questions">
                {busy === "questions" ? "Saving…" : "Save question status"}</button>
            )}
          </div>

          {editable && (
            <button className="tp-btn" style={{ marginTop: 16, width: "100%", padding: 16 }} onClick={() => setStep("review")} data-testid="hwf-to-review">
              Review &amp; submit <ArrowRight size={16} />
            </button>
          )}
          {!editable && <p className="tp-pill ok" style={{ marginTop: 16 }} data-testid="hwf-locked">This submission is locked — {STATUS_LABEL[sub.status]}.</p>}
        </>
      )}

      {step === "review" && sub && (
        <div className="tp-panel" style={{ marginTop: 14 }} data-testid="hwf-review">
          <h2 className="tp-h2" style={{ fontSize: 22, margin: 0 }}>Review before submitting</h2>
          <div className="tp-grid-2" style={{ marginTop: 12, fontSize: 13, color: "#1e3a5f" }} data-testid="hwf-review-info">
            <p style={{ margin: 4 }}><b>Student:</b> {account.name}</p>
            <p style={{ margin: 4 }}><b>Class:</b> {CLASS_LABEL[sub.category]}</p>
            <p style={{ margin: 4 }}><b>Subject:</b> {sub.subject}</p>
            <p style={{ margin: 4 }}><b>Chapter:</b> {sub.chapter}</p>
            <p style={{ margin: 4 }}><b>Topic:</b> {sub.topic}</p>
            <p style={{ margin: 4 }}><b>Subtopic:</b> {sub.subtopic || "—"}</p>
            <p style={{ margin: 4 }}><b>Homework:</b> {sub.title}</p>
            <p style={{ margin: 4 }}><b>Date:</b> {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
          </div>
          <div className="tp-grid-3" style={{ marginTop: 14 }}>
            {SLOTS.map(([slot, label]) => {
              const d = docs[slot];
              return (
                <div className="tp-card" key={slot} style={{ border: d ? "1.5px solid #12b76a" : "1.5px dashed #cbd5e1" }} data-testid={`hwf-check-${slot}`}>
                  <b style={{ fontSize: 13, color: "#0b2545" }}>{label}</b>
                  {d ? (
                    <p className="tp-muted" style={{ fontSize: 12, margin: "6px 0" }}>{d.name} · {d.pages} page(s)</p>
                  ) : (
                    <p style={{ fontSize: 12, margin: "6px 0", color: slot === "solution" ? "#b45309" : "#b42318" }} data-testid={`hwf-missing-${slot}`}>
                      {slot === "solution" ? "Not uploaded (optional)" : "Missing — required"}</p>
                  )}
                  {d && <a className="tp-btn small ghost" href={`${API}/api/hwd/files/${sub.submission_id}/${slot}`} target="_blank" rel="noreferrer" data-testid={`hwf-check-preview-${slot}`}>Preview</a>}
                  {" "}
                  <button className="tp-btn small ghost" onClick={() => setStep("workspace")} data-testid={`hwf-check-edit-${slot}`}>{d ? "Replace" : "Upload"}</button>
                </div>
              );
            })}
          </div>
          <div className="tp-grid-4" style={{ marginTop: 14 }} data-testid="hwf-summary">
            {[["Total tracked", summary.total], ["Solved confidently", summary.solved_confidently],
              ["Attempted but unsure", summary.attempted_unsure], ["Not attempted", summary.not_attempted],
              ["Needs doubt help", summary.doubt]].map(([l, v]) => (
              <div className="tp-card" key={l} style={{ textAlign: "center" }}>
                <b style={{ fontSize: 20 }}>{v}</b>
                <p className="tp-muted" style={{ margin: 0, fontSize: 11 }}>{l}</p>
              </div>
            ))}
          </div>
          <div className="admin-row" style={{ marginTop: 16, gap: 10, flexWrap: "wrap" }}>
            <button className="tp-btn ghost" onClick={() => setStep("workspace")} data-testid="hwf-review-back">← Keep editing</button>
            <button className="tp-btn" style={{ padding: "14px 22px", fontSize: 15 }} disabled={!docs.question_paper || !docs.attempted || busy === "submit"}
                    onClick={finalSubmit} data-testid="hwf-final-submit">
              <CheckCircle2 size={17} /> {busy === "submit" ? "Submitting…" : "SUBMIT HOMEWORK FOR EVALUATION"}
            </button>
          </div>
          {(!docs.question_paper || !docs.attempted) && (
            <p className="tp-note" style={{ color: "#b42318", marginTop: 8 }} data-testid="hwf-review-blocked">Question paper and your solved homework are both required. The solution PDF stays optional.</p>
          )}
        </div>
      )}

      {step === "done" && sub && (
        <div className="tp-panel" style={{ marginTop: 16, border: "2px solid #12b76a", background: "#f0fdf4", textAlign: "center", padding: 28 }} data-testid="hwf-done">
          <CheckCircle2 size={44} style={{ color: "#12b76a", margin: "0 auto" }} />
          <h2 className="tp-h2" style={{ fontSize: 24, margin: "12px 0 6px" }}>Homework submitted for evaluation!</h2>
          <p className="tp-muted">Submission ID: <b data-testid="hwf-done-id">{sub.submission_id}</b></p>
          <p className="tp-muted" style={{ fontSize: 13 }}>Pranjal Sir will personally review your work and publish your marks out of 10 with question-wise feedback. You'll get an email when it's checked.</p>
          {plannerRef && <p className="tp-pill ok" data-testid="hwf-done-planner">Timetable updated — that homework block is ticked off in your Study Planner.</p>}
          <div className="admin-row" style={{ justifyContent: "center", gap: 10, marginTop: 14 }}>
            <a className="tp-btn" href={`/homework/hw/${sub.submission_id}`} data-testid="hwf-done-view">View submission</a>
            {plannerRef
              ? <a className="tp-btn ghost" href="/homework/planner" data-testid="hwf-done-planner-link">Back to my timetable</a>
              : <a className="tp-btn ghost" href="/homework" data-testid="hwf-done-home">Back to dashboard</a>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function HomeworkFlow() {
  return <HomeworkShell><FlowInner /></HomeworkShell>;
}
